import React, { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Select from 'react-select';
import ErrorIcon from '@material-ui/icons/Error';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import ProfileStore from 'stores/ProfileStore';
import { ACCESS_LEVEL_STR } from 'pages/User/AccessLevel';
import RegistrationGoldModal from 'components/RegistrationGoldModal/RegistrationGoldModal';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { MESSAGE_TYPES } from 'enum/messages';
import { CONTEXTUAL_GOLD_MODAL_AREAS } from 'utils/consts';

import Conversations from './Conversations/Conversations';
import Option from './Option';
import styles from './MultiplePrivateChat.css';


function MultiplePrivateChat(props) {
  const { user, isBasicUser } = UserStore;
  const { isLoggedInUserRoomModerator } = FirebaseStore;
  const { performerTopTipper } = TippingStore;
  const {
    paymentActions: { toggleRegisterGoldOverlay },
    isRegisterGoldOverlayVisible,
    isBroadcasterChat,
  } = props;
  const {
    privateChatSessionData: { username, roomName },
    chatSettings,
    chatSettingsPerformer,
  } = ChatStore;
  const options = FirebaseStore.sortedUsers
    .map((sortedUser) => {
      return {
        value: sortedUser.originalUsername,
        label: sortedUser.originalUsername,
        performerTopTipper,
        ...sortedUser,
      };
    })
    .filter(
      (item) => {
        const isMyselfOrCurrentPerformer = [username, roomName].includes(item.value.toLowerCase());
        const isGuest = item.accessLevel === ACCESS_LEVEL_STR.GUEST;
        const isAnonymous = parseInt(item.stealthLevel, 10) > 0;

        return (!isMyselfOrCurrentPerformer && !isGuest && !isAnonymous);
      }
    );

  const { privateUserChats, activeConversation, setActiveConversation, startConversation, activeReadConversations } =
    ChatStore;
  const { allowPrivateMessagesFromOtherViewers } = chatSettings;
  const { disablePrivateChat } = chatSettingsPerformer;
  const multiplePrivatechatEnabled = isBroadcasterChat ? !disablePrivateChat : allowPrivateMessagesFromOtherViewers;
  const showConversationList = Object.keys(privateUserChats).length > 0;
  const chatTabsList = Object.keys(privateUserChats).filter(
    (key) => key.length > 0 && key && key.toLowerCase() !== roomName.toLowerCase()
  );
  const hasAdminMessages = chatTabsList.find((key) => {
    const userLevel = privateUserChats[key].accessLevel;

    return (
      userLevel === MESSAGE_TYPES.ADMIN ||
      userLevel === MESSAGE_TYPES.MODERATOR ||
      userLevel === MESSAGE_TYPES.USER_MODERATOR
    );
  });
  const showList =
    (multiplePrivatechatEnabled && showConversationList) || (hasAdminMessages && !multiplePrivatechatEnabled);

  const handleGuestOpenGoldOverlay = useCallback(() => {
    ModalService.hideModal();
    toggleRegisterGoldOverlay(true);
  }, [toggleRegisterGoldOverlay]);

  const handleUserSelect = useCallback((e) => {
    const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (!user.username || isGuestBilling) {
      const modalProps = {
        area: CONTEXTUAL_GOLD_MODAL_AREAS.PRIVATE_MESSAGING,
        onMembershipClick: handleGuestOpenGoldOverlay,
      };

      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        modalProps
      );
    } else if (UserStore.user.accessLevel < ACCESS_LEVEL_NUM.GOLD) {
      if ((isBasicUser && isLoggedInUserRoomModerator(user.username)) || isBroadcasterChat) {
        startConversation(e);
      } else {
        ModalService.showModal(
          ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
          { area: CONTEXTUAL_GOLD_MODAL_AREAS.PRIVATE_MESSAGING }
        );
      }
    } else {
      startConversation(e);
    }
  }, [isBasicUser, isBroadcasterChat, isLoggedInUserRoomModerator, user.accessLevel, user.username,
    startConversation, handleGuestOpenGoldOverlay]);

  const closeGoldRegisterModal = useCallback(() => {
    toggleRegisterGoldOverlay(false);
  }, [toggleRegisterGoldOverlay]);

  const placeholder = isBroadcasterChat ? (
    translate('v2broadcast.find.user.your.room')) : translate('v2profile.private.messages.search.placeholder');

  const onEnablePrivateWithOtherUsers = useCallback(() => {
    const { accessHash } = user;
    const { toggleChatFilters, togglePerformerChatFilters } = ChatStore;
    const { profileName } = ProfileStore;

    if (isBroadcasterChat) {
      const filter = { disablePrivateChat: false };
      togglePerformerChatFilters(filter, user.username, accessHash);
    } else if (user.username) {
      const filter = { allowPrivateMessagesFromOtherViewers: true };
      toggleChatFilters(filter, user.username, accessHash, profileName);
    }

  }, [isBroadcasterChat, user]);

  const enablePrivateChatWithOtherUsersElement = useMemo(() => {
    const enableButton = (
      <button
        className={styles.enablePrivateChat}
        onClick={onEnablePrivateWithOtherUsers}
      >
        {translate('v2profile.privateChat.button.enable')}
      </button>
    );

    return (
      <div>
        <div>
          {translate('v2profile.privateChat.withUsers.isDisabled')}
        </div>
        <div>
          {translate('v2profile.privateChat.prompt.enable')}
          {enableButton}
        </div>
      </div>
    );
  }, [onEnablePrivateWithOtherUsers]);

  return (
    <Fragment>
      <h5 className={styles.title}>{translate('v2profile.private.messages.title')}</h5>
      {!multiplePrivatechatEnabled && user.username ? (
        <div className={styles.chatDisabled}>
          <ErrorIcon className={styles.chatDisabledIcon} />
          {enablePrivateChatWithOtherUsersElement}
        </div>
      ) : (
        <Select
          autoBlur={true}
          className={styles.select}
          optionRenderer={isBroadcasterChat ? Option : null}
          options={options}
          placeholder={placeholder}
          onChange={handleUserSelect}
        />
      )}

      {showList && (
        <Conversations
          activeConversationKey={activeConversation}
          activeReadConversations={activeReadConversations}
          list={privateUserChats}
          onchangeTab={setActiveConversation}
          options={options}
          roomName={roomName}
          user={props.user}
          {...props}
        />
      )}
      {!showConversationList && multiplePrivatechatEnabled && (
        <div className={styles.empty}>
          {isBroadcasterChat ? (
            translate('v2broadcast.you.have.no.messages')) : translate('v2profile.no.private.conversations')}
        </div>
      )}
      {isRegisterGoldOverlayVisible && (
        <RegistrationGoldModal
          history={history}
          showRegistrationGoldModal={isRegisterGoldOverlayVisible}
          onClose={closeGoldRegisterModal}
        />
      )}
    </Fragment>
  );
}

MultiplePrivateChat.propTypes = {
  isBroadcasterChat: PropTypes.bool,
  isRegisterGoldOverlayVisible: PropTypes.bool,
  match: PropTypes.object,
  paymentActions: PropTypes.object,
  user: PropTypes.object,
  walletDetails: PropTypes.object,
};

MultiplePrivateChat.defaultProps = {
  isBroadcasterChat: false,
  isRegisterGoldOverlayVisible: false,
  match: {},
  paymentActions: {},
  user: {},
  walletDetails: {},
};

export default observer(MultiplePrivateChat);
